import LogoutIcon from "@mui/icons-material/Logout";
import MoreIcon from "@mui/icons-material/MoreVert";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import TuneIcon from "@mui/icons-material/Tune";
import { Tooltip, useMediaQuery, useTheme } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { alpha } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ArtemisLogo from "../../assets/images/artemis-headhunting_logo-only-purple.svg?react";
import { useAuth } from "../../context/AuthContext";
import { formatDateTime, formatRelativeTimeShort } from "../../utils/date-formatting";
import { useGlobalState } from "../globalState";
import { ImpersonateDialog } from "../ImpersonateDialog";
import { Drawer } from "./drawer";
import { EnvironmentSwitcher } from "./environment-switcher";
import { MigrationBadge } from "./migration-badge";
import { NavbarBadge } from "./navbar-badge";
import { PersonSearch } from "./person-search";

interface ManagedUser {
	id: number;
	username: string;
	first_name: string;
	last_name: string;
	last_todo: string | null;
	group_name: string;
}

export const Navbar = () => {
	const [openMenuName, setOpenMenuName] = useState(null);
	const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState(null);
	const [menuOpen, setMenuOpen] = useState<boolean>(false);
	const [showImpersonate, setShowImpersonate] = useState(false);
	const [managedUsers, setManagedUsers] = useState<ManagedUser[]>([]);
	const theme = useTheme();
	const desktop = useMediaQuery(theme.breakpoints.up("md"));
	const { logout, isImpersonating } = useAuth();
	const {
		currentUser,
		databaseHost,
		databaseName,
		gitBranch,
		gitCommit,
		gitCommitVersion,
		gitCommitDate,
		stagingEnv,
		stagingHostnames,
		migrations,
	} = useGlobalState();
	const navigate = useNavigate();

	useEffect(() => {
		if (!currentUser?.is_staff && !currentUser?.is_superuser) return;
		const fetchActive = () => {
			fetch("/api/v2/users/managed_users/")
				.then((res) => res.json())
				.then((data) =>
					setManagedUsers(
						data.map(
							(u: {
								id: number;
								username: string;
								first_name: string;
								last_name: string;
								last_todo: string | null;
								group_name: string;
							}) => ({
								id: u.id,
								username: u.username,
								first_name: u.first_name,
								last_name: u.last_name,
								last_todo: u.last_todo,
								group_name: u.group_name,
							})
						)
					)
				)
				.catch((err) => console.error("Failed to fetch managed users:", err));
		};
		fetchActive();
		const interval = setInterval(fetchActive, 60_000);
		return () => clearInterval(interval);
	}, [currentUser?.is_staff, currentUser?.is_superuser]);

	const activeUserStats = useMemo(() => {
		if (!managedUsers.length) return { count60m: 0, users1h: [], users72h: [] };
		const now = new Date();
		const window60m = new Date(now.getTime() - 60 * 60 * 1000);
		const window72h = new Date(now.getTime() - 72 * 60 * 60 * 1000);

		const sorted = managedUsers
			.filter(
				(u) =>
					u.group_name !== "Artemis" &&
					u.last_todo &&
					new Date(u.last_todo) >= window72h
			)
			.sort(
				(a, b) =>
					new Date(b.last_todo || 0).getTime() -
					new Date(a.last_todo || 0).getTime()
			);

		const users1h = sorted.filter(
			(u) => u.last_todo && new Date(u.last_todo) >= window60m
		);
		const users72h = sorted.filter(
			(u) => u.last_todo && new Date(u.last_todo) < window60m
		);

		return { count60m: users1h.length, users1h, users72h };
	}, [managedUsers]);

	const handleMobileMenuClose = () => {
		setMobileMoreAnchorEl(null);
	};

	const handleMobileMenuOpen = (event, menuName: string) => {
		setMobileMoreAnchorEl(event.currentTarget);
		setOpenMenuName(menuName);
	};

	const shouldShowMenu = (name: string) => {
		return name == openMenuName && Boolean(mobileMoreAnchorEl);
	};

	const handleLogout = () => {
		logout();
		navigate("/");
		handleMobileMenuClose();
	};

	return (
		<Box
			sx={{
				flexGrow: 1,
			}}
		>
			<AppBar position="static" elevation={0} sx={{ padding: 0 }}>
				<Toolbar sx={{ flexWrap: "wrap", rowGap: 1, minHeight: "auto" }}>
					<IconButton
						id="drawer-toggle-button"
						edge="start"
						sx={{
							marginRight: (theme) => theme.spacing(2),
						}}
						color="inherit"
						aria-label="open drawer"
						onClick={(ev) => {
							setMenuOpen(!menuOpen);
						}}
					>
						<ArtemisLogo
							data-component="navbar-logo"
							style={{ fill: theme.logo.small, width: 24, height: 24 }}
						/>
					</IconButton>

					{desktop ? (
						<Box
							display="flex"
							alignItems="center"
							gap={1}
							flexWrap="wrap"
							sx={{ rowGap: 1, minWidth: 0 }}
						>
							<Typography variant="h6" noWrap>
								ODI
							</Typography>
							{(currentUser?.is_staff || currentUser?.is_superuser) && (
								<Box
									display="flex"
									alignItems="center"
									gap={0.75}
									data-component="system-info"
								>
									{gitCommit && gitCommitVersion && (
										<Tooltip
											title={
												<Box sx={{ fontFamily: "monospace" }}>
													<Box>
														Version {gitCommitVersion}
													</Box>
													<Box>Hash {gitCommit}</Box>
													{gitCommitDate && (
														<Box>
															Date{" "}
															{formatDateTime(
																gitCommitDate
															)}
														</Box>
													)}
												</Box>
											}
											arrow
										>
											<NavbarBadge dataComponent="app-version">
												{gitCommitVersion}
											</NavbarBadge>
										</Tooltip>
									)}
									{currentUser?.is_superuser && (
										<>
											{databaseHost && (
												<>
													<NavbarBadge
														dataComponent="db-name"
														bgcolor={
															databaseName === "odi"
																? theme.palette.error
																		.main
																: undefined
														}
													>
														🗄 {databaseName}
													</NavbarBadge>
													<NavbarBadge dataComponent="db-host">
														{databaseHost}
													</NavbarBadge>
												</>
											)}
											{gitBranch && (
												<Tooltip title={gitCommit || ""} arrow>
													<NavbarBadge dataComponent="git-branch">
														⎇ {gitBranch}
													</NavbarBadge>
												</Tooltip>
											)}
											{migrations && (
												<MigrationBadge
													migrations={migrations}
												/>
											)}
											<EnvironmentSwitcher
												stagingEnv={stagingEnv}
												stagingHostnames={stagingHostnames}
											/>
										</>
									)}
								</Box>
							)}
							{(currentUser?.is_staff || currentUser?.is_superuser) && (
								<Tooltip
									title={
										<Box
											sx={{
												fontSize: "0.875rem",
												maxHeight: "300px",
												overflowY: "auto",
											}}
										>
											<Typography
												variant="subtitle2"
												sx={{ mb: 1, fontWeight: 600 }}
											>
												Active last 1h:
											</Typography>
											{activeUserStats.users1h.length > 0 ? (
												<Box
													sx={{
														display: "flex",
														flexDirection: "column",
														gap: 0.5,
														mb: 1.5,
													}}
												>
													{activeUserStats.users1h.map(
														(user) => (
															<Box
																key={user.id}
																sx={{
																	fontSize: "0.8rem",
																	color: "inherit",
																}}
															>
																{user.first_name ||
																user.last_name
																	? `${user.first_name} ${user.last_name}`.trim()
																	: user.username}
																{" - "}
																{formatRelativeTimeShort(
																	user.last_todo
																)}
															</Box>
														)
													)}
												</Box>
											) : (
												<Box
													sx={{ fontSize: "0.8rem", mb: 1.5 }}
												>
													No active users
												</Box>
											)}
											<Typography
												variant="subtitle2"
												sx={{ mb: 1, fontWeight: 600 }}
											>
												Active last 72h:
											</Typography>
											{activeUserStats.users72h.length > 0 ? (
												<Box
													sx={{
														display: "flex",
														flexDirection: "column",
														gap: 0.5,
													}}
												>
													{activeUserStats.users72h.map(
														(user) => (
															<Box
																key={user.id}
																sx={{
																	fontSize: "0.8rem",
																	color: "inherit",
																}}
															>
																{user.first_name ||
																user.last_name
																	? `${user.first_name} ${user.last_name}`.trim()
																	: user.username}
																{" - "}
																{formatRelativeTimeShort(
																	user.last_todo
																)}
															</Box>
														)
													)}
												</Box>
											) : (
												<Box sx={{ fontSize: "0.8rem" }}>
													No active users
												</Box>
											)}
										</Box>
									}
									arrow
								>
									<Box
										sx={{
											px: 1.25,
											py: 0.5,
											borderRadius: 1,
											bgcolor:
												activeUserStats.count60m > 0
													? theme.palette.success.light
													: alpha(
															theme.palette.common.white,
															0.15
														),
											fontFamily: "monospace",
											fontSize: "0.85rem",
											color: alpha(
												theme.palette.common.white,
												0.9
											),
											whiteSpace: "nowrap",
											cursor: "default",
											display: "flex",
											alignItems: "center",
											gap: 0.5,
										}}
										data-component="active-users-pill"
									>
										<PeopleIcon sx={{ fontSize: "1rem" }} />
										{activeUserStats.count60m}
									</Box>
								</Tooltip>
							)}
						</Box>
					) : null}
					<Box
						sx={{
							flexGrow: 1,
						}}
					/>
					<Box>
						<PersonSearch />
					</Box>

					<Box
						sx={{
							marginLeft: (theme) => theme.spacing(2),
						}}
					>
						<IconButton
							aria-label="show more"
							aria-haspopup="true"
							onClick={(ev) => {
								handleMobileMenuOpen(ev, "profile");
							}}
							color="inherit"
						>
							<MoreIcon />
						</IconButton>
					</Box>
				</Toolbar>
			</AppBar>

			<Drawer
				open={menuOpen}
				setClosed={() => {
					setMenuOpen(false);
				}}
			/>

			<Menu
				anchorEl={mobileMoreAnchorEl}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				keepMounted
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				open={shouldShowMenu("profile")}
				onClose={handleMobileMenuClose}
			>
				{currentUser?.userprofile?.can_impersonate && !isImpersonating && (
					<MenuItem
						onClick={() => {
							setShowImpersonate(true);
							handleMobileMenuClose();
						}}
					>
						<ListItemIcon>
							<SwitchAccountIcon />
						</ListItemIcon>
						<p>Impersonate User</p>
					</MenuItem>
				)}
				<MenuItem component={Link} to="/config" onClick={handleMobileMenuClose}>
					<ListItemIcon>
						<SettingsIcon />
					</ListItemIcon>
					<p>Settings</p>
				</MenuItem>
				{currentUser?.userprofile?.up_group?.name === "Artemis" && (
					<MenuItem
						component={Link}
						to="/system-settings"
						onClick={handleMobileMenuClose}
					>
						<ListItemIcon>
							<TuneIcon />
						</ListItemIcon>
						<p>System Settings</p>
					</MenuItem>
				)}
				<MenuItem onClick={handleLogout}>
					<ListItemIcon>
						<LogoutIcon />
					</ListItemIcon>
					<p>Log out</p>
				</MenuItem>
			</Menu>

			<ImpersonateDialog
				open={showImpersonate}
				onClose={() => setShowImpersonate(false)}
			/>
		</Box>
	);
};
